import React, { useState } from 'react';
import './molecules.css';

interface PermissionsScreenProps {
  onContinue: () => void;
  onSkip?: () => void;
}

export const PermissionsScreen: React.FC<PermissionsScreenProps> = ({
  onContinue,
  onSkip,
}) => {
  const [cameraEnabled, setCameraEnabled] = useState(true);
  const [notifsEnabled, setNotifsEnabled] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleContinue = async () => {
    if (cameraEnabled) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        stream.getTracks().forEach(track => track.stop());
      } catch (err) {
        setError('Please enable camera and microphone in your browser settings, then try again.');
        return;
      }
    }
    onContinue();
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-[#E8A09A]">
      {/* Top left cluster - Zigzag with UP wave */}
      <div className="mol-bloom" style={{position:'absolute',top:80,left:28,pointerEvents:'none'}}>
        <svg width="50" height="55" viewBox="-5 -30 70 90">
          <circle cx="12" cy="8" r="3" fill="#fff"/>
          <circle cx="32" cy="28" r="4" fill="#fff"/>
          <circle cx="52" cy="48" r="3" fill="#fff"/>
          <line x1="12" y1="8" x2="32" y2="28" stroke="#fff" strokeWidth="2"/>
          <line x1="32" y1="28" x2="52" y2="48" stroke="#fff" strokeWidth="2"/>
          <path d="M12,5 Q22,-5 12,-15 Q22,-25 12,-35" stroke="#1a1814" strokeWidth="3" fill="none" strokeLinecap="round"/>
        </svg>
      </div>

      {/* Top right cluster - Vertical pair with diagonal waves */}
      <div className="mol-bloom-d1" style={{position:'absolute',top:90,right:28,pointerEvents:'none'}}>
        <svg width="45" height="65" viewBox="-15 -20 75 105">
          <circle cx="20" cy="12" r="4" fill="#fff"/>
          <circle cx="20" cy="52" r="4" fill="#fff"/>
          <line x1="20" y1="12" x2="20" y2="52" stroke="#fff" strokeWidth="2"/>
          <path d="M24,8 Q32,8 36,0 Q32,-8 40,-12 Q40,-20 48,-24" stroke="#1a1814" strokeWidth="3" fill="none" strokeLinecap="round"/>
          <path d="M24,56 Q32,56 36,64 Q32,72 40,76" stroke="#1a1814" strokeWidth="3" fill="none" strokeLinecap="round"/>
        </svg>
      </div>

      {/* Bottom left cluster - Angled pair with diagonal DOWN-LEFT wave */}
      <div className="mol-bloom-d2" style={{position:'absolute',bottom:55,left:35,pointerEvents:'none'}}>
        <svg width="55" height="50" viewBox="-20 0 90 70">
          <circle cx="12" cy="18" r="3" fill="#fff"/>
          <circle cx="42" cy="42" r="4" fill="#fff"/>
          <line x1="12" y1="18" x2="42" y2="42" stroke="#fff" strokeWidth="2"/>
          <path d="M38,46 Q30,46 26,54 Q30,62 22,66" stroke="#1a1814" strokeWidth="3" fill="none" strokeLinecap="round"/>
        </svg>
      </div>

      {/* Progress bar — 3 bars, first 2 active */}
      <div className="relative z-10 flex gap-2 px-6 pt-[55px]">
        <div className="flex-1 h-[3px] rounded-full bg-[#1A1814]" />
        <div className="flex-1 h-[3px] rounded-full bg-[#1A1814]" />
        <div className="flex-1 h-[3px] rounded-full bg-[#1A1814]/25" />
      </div>

      {/* Content */}
      <div className="relative z-10 mt-[100px] px-6">
        {/* Camera icon badge */}
        <div className="w-[52px] h-[52px] bg-white/25 rounded-xl flex items-center justify-center mb-5 anim-stagger-1">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#1A1814" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="5" width="14" height="12" rx="2"/>
            <path d="M16 10l4-2v8l-4-2"/>
          </svg>
        </div>

        {/* Step label */}
        <p className="text-[#1A1814]/50 text-[11px] tracking-[1.5px] uppercase m-0 mb-1.5">
          Step 2 of 3
        </p>

        {/* Heading */}
        <h1 className="text-[#1A1814] text-[26px] font-semibold m-0 mb-2.5 leading-[1.2] anim-stagger-1">
          Enable camera<br/>& microphone
        </h1>

        {/* Body */}
        <p className="text-[#1A1814]/60 text-[14px] leading-relaxed m-0 mb-6">
          We need access to capture photos, videos, and audio for your responses.
        </p>

        {/* Camera & Microphone toggle card */}
        <div className="bg-white/30 rounded-2xl p-4 mb-3 anim-stagger-2">
          <div className="flex items-center gap-3.5">
            <div className="w-11 h-11 bg-[#D4715A] rounded-[10px] flex items-center justify-center flex-shrink-0">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="5" width="14" height="12" rx="2"/>
                <path d="M16 10l4-2v8l-4-2"/>
              </svg>
            </div>
            <div className="flex-1">
              <div className="text-[#1A1814] text-[15px] font-medium">Camera & Microphone</div>
              <div className="text-[#1A1814]/50 text-[12px]">Required for photos, videos & audio</div>
            </div>
            <button
              onClick={() => setCameraEnabled(!cameraEnabled)}
              className={`w-12 h-7 rounded-full relative transition-colors ${
                cameraEnabled ? 'bg-[#6E7A5A]' : 'bg-[#1A1814]/20'
              }`}
            >
              <div
                className={`w-[22px] h-[22px] bg-white rounded-full absolute top-[3px] shadow-sm transition-all ${
                  cameraEnabled ? 'right-[3px]' : 'left-[3px]'
                }`}
              />
            </button>
          </div>
        </div>

        {/* Notifications toggle card */}
        <div className="bg-white/30 rounded-2xl p-4 mb-4">
          <div className="flex items-center gap-3.5">
            <div className="w-11 h-11 bg-[#1A1814]/10 rounded-[10px] flex items-center justify-center flex-shrink-0">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1A1814" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                <path d="M13.73 21a2 2 0 01-3.46 0"/>
              </svg>
            </div>
            <div className="flex-1">
              <div className="text-[#1A1814] text-[15px] font-medium">Notifications</div>
              <div className="text-[#1A1814]/50 text-[12px]">Get reminders for new tasks</div>
            </div>
            <button
              onClick={() => setNotifsEnabled(!notifsEnabled)}
              className={`w-12 h-7 rounded-full relative transition-colors ${
                notifsEnabled ? 'bg-[#6E7A5A]' : 'bg-[#1A1814]/20'
              }`}
            >
              <div
                className={`w-[22px] h-[22px] bg-white rounded-full absolute top-[3px] shadow-sm transition-all ${
                  notifsEnabled ? 'right-[3px]' : 'left-[3px]'
                }`}
              />
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 p-3 bg-[#D4715A]/15 rounded-xl text-[#D4715A] text-sm text-center">
            {error}
          </div>
        )}

        {/* Assurance message */}
        <div className="bg-white/20 rounded-xl p-3.5 mb-6">
          <div className="flex gap-2.5">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgba(26,24,20,0.6)" strokeWidth="1.5" className="flex-shrink-0 mt-0.5">
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 16v-4M12 8h.01"/>
            </svg>
            <p className="text-[#1A1814]/70 text-[13px] leading-relaxed m-0">
              Your browser will ask for permission. Tap "Allow" to continue.{' '}
              <strong className="text-[#1A1814]">We respect your privacy</strong> — your media is only used for research responses and we'll never spam you with notifications.
            </p>
          </div>
        </div>

        {/* Continue button */}
        <button
          onClick={handleContinue}
          className="w-full bg-[#1A1814] text-[#F5F3F0] border-none rounded-xl py-4 text-[15px] font-medium cursor-pointer flex items-center justify-center gap-2 mb-3.5 anim-stagger-3"
        >
          CONTINUE
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M5 12h14M12 5l7 7-7 7"/>
          </svg>
        </button>

        {/* Skip link */}
        <p className="text-center m-0 pb-8">
          <button
            onClick={onSkip || onContinue}
            className="text-[#1A1814]/50 text-[13px] underline bg-transparent border-none cursor-pointer"
          >
            Maybe later
          </button>
        </p>
      </div>
    </div>
  );
};

export default PermissionsScreen;
